// The RSVP control: three status buttons, the live headcount, and the day-of
// "still in?" confirmation.
//
// Used on the event detail page and inline on the event-box cards, from one
// definition, because the rules below are not presentation choices:
//
//   • RSVP round-trips WITHOUT navigation. The save response carries fresh
//     counts, so nothing here refetches the event.
//   • The two headcounts are always shown as two numbers. Advance yeses and
//     day-of confirmations answer different questions and are never summed.
//   • The confirm button is offered under the SAME rule the server uses to
//     accept it (`eventHasEnded()` from @msc/shared). A second opinion here
//     would produce a button that errors on tap.
//   • A closed event shows no controls at all — closing freezes the headcount,
//     and the server returns EVENT_CLOSED to anyone who tries anyway.
//
// The day-of prompt sits at the RIGHT END OF THE STATUS ROW, not in a block of
// its own below it. It used to be a "Still coming?" label plus a full-width
// button, which on a feed card — a surface whose whole job is to stay small —
// was three lines of furniture for one tap, and stayed that tall after
// confirming, when there is nothing left to tap at all. Changed 2026-09-09.
//
// It is also shown far less often now: an RSVP made on the event's own day is
// itself the confirmation (the server stamps it, see rsvp/queries.ts), so the
// prompt only appears for someone who said yes IN ADVANCE and has not been back
// since. That is the case it was always for.

import { ApiClientError, confirmDayof, saveRsvp } from '../api/client.js';
import { el } from '../dom.js';
import {
  eventHasEnded,
  eventTypeMessage,
  fmtInstant,
  RSVP_STATUSES,
  rsvpStatusLabel,
  TimeFormat,
  type EventSummary,
  type RsvpCounts,
  type RsvpStatus,
} from '@msc/shared';

export interface RsvpControlOptions {
  event: EventSummary;
  /** Initial state. Usually taken straight off the event detail payload. */
  counts: RsvpCounts;
  /** The viewer's display zone, for rendering the confirmation timestamp. */
  viewerZone: string;
  /** Called after any successful save or confirm, with the fresh counts. */
  onChange?: (counts: RsvpCounts) => void;
  /** True trims the headcount line down for a card. */
  compact?: boolean;
  /**
   * Write the capacity message ("minimum of 3 met", "needs 3", "Full") into this
   * element, owned and placed by the caller, instead of at the end of the
   * headcount line. The Next up panel uses it, at the bottom right.
   */
  noteTarget?: HTMLElement;
  /**
   * Render read-only for a reason other than the event being closed — today, a
   * suspended group. The server refuses every write while it is set, so offering
   * buttons would only produce a tap that errors.
   *
   * The caller supplies the sentence, because the reason is the caller's to
   * explain: "closed" and "suspended" are not the same story.
   */
  frozenNote?: string;
}

/** How early the day-of confirm button appears. */
const CONFIRM_WINDOW_MS = 24 * 60 * 60 * 1000;

export function rsvpControl(opts: RsvpControlOptions): HTMLElement {
  const { event, viewerZone } = opts;
  let counts = opts.counts;

  const root = el('div', { class: 'rsvp-control' });

  // Closed takes precedence: a closed event in a suspended group is still
  // closed, and that is the more specific thing to say.
  const frozen = event.closedAt
    ? 'RSVPs are closed for this event.'
    : (opts.frozenNote ?? null);
  if (frozen) {
    root.append(
      el('p', { class: 'muted small' }, frozen),
      headcountLine(counts, opts.compact === true),
    );
    return root;
  }

  const status = el('p', { class: 'form-error', hidden: true });
  const headcount = headcountLine(counts, opts.compact === true, opts.noteTarget !== undefined);
  const buttons = el('div', { class: 'rsvp-buttons' });
  // Lives INSIDE the button row, pushed right by CSS. renderButtons() calls
  // replaceChildren on that row, so the slot has to be re-appended there every
  // time or the first status change silently drops it off the page.
  const confirmSlot = el('div', { class: 'rsvp-confirm' });

  function setBusy(busy: boolean): void {
    // `:scope >` so the confirm pill, which now lives in this row, is not
    // disabled by a status save it has nothing to do with.
    for (const b of buttons.querySelectorAll(':scope > button')) {
      if (busy) b.setAttribute('disabled', 'disabled');
      else b.removeAttribute('disabled');
    }
  }

  function apply(next: RsvpCounts): void {
    counts = next;
    headcount.replaceChildren(
      ...headcountLine(counts, opts.compact === true, opts.noteTarget !== undefined).childNodes,
    );
    renderButtons();
    renderConfirm();
    opts.onChange?.(counts);
  }

  function fail(err: unknown): void {
    // Branch on the stable code, never the message.
    const code = err instanceof ApiClientError ? err.code : null;
    status.textContent =
      code === 'EVENT_FULL'
        ? 'This event is full.'
        : code === 'EVENT_CLOSED'
          ? 'This event has been closed, so RSVPs are frozen.'
          : code === 'EVENT_ENDED'
            ? 'That event has already ended.'
            : code === 'RSVP_NOT_YES'
              ? 'RSVP yes first, then confirm.'
              : err instanceof ApiClientError
                ? err.message
                : 'Could not save that. Try again.';
    status.hidden = false;
  }

  function renderButtons(): void {
    const mine = counts.myRsvp?.status ?? null;
    // "Full" hides only the yes button, and only for someone not already in.
    // Someone who said yes must still be able to change their mind, and the
    // server's cap check excludes their own row for exactly that reason.
    const yesBlocked = counts.isFull && mine !== 'yes';

    buttons.replaceChildren(
      ...RSVP_STATUSES.filter((s) => !(s === 'yes' && yesBlocked)).map((s) => {
        const active = mine === s;
        const btn = el(
          'button',
          {
            type: 'button',
            class: `btn btn-rsvp${active ? ' btn-rsvp-active' : ''}`,
            'aria-pressed': active ? 'true' : 'false',
          },
          rsvpStatusLabel(s),
        );
        btn.addEventListener('click', async () => {
          status.hidden = true;
          setBusy(true);
          try {
            const result = await saveRsvp(event.eventId, s);
            apply(result.counts);
            // Acknowledge a lifecycle hook the moment it fires. The group also
            // gets the post; this is just so the person who tipped it over sees
            // that they did.
            if (result.hooksFired.length > 0) {
              status.textContent = result.hooksFired.includes('full')
                ? "That filled it up. The group has been told."
                : 'That hit the minimum. The group has been told.';
              status.className = 'form-ok';
              status.hidden = false;
            }
          } catch (err) {
            status.className = 'form-error';
            fail(err);
          } finally {
            setBusy(false);
          }
        });
        return btn;
      }),
    );

    if (yesBlocked && opts.noteTarget === undefined) {
      buttons.append(el('span', { class: 'muted small' }, 'Full, no seats left'));
    }

    // replaceChildren above just detached it. Put it back, or the prompt
    // vanishes the moment anyone touches a status button.
    buttons.append(confirmSlot);

    if (opts.noteTarget !== undefined) {
      const text = capacityNote(event, counts);
      opts.noteTarget.textContent = text;
      opts.noteTarget.hidden = text === '';
    }
  }

  function renderConfirm(): void {
    const mine = counts.myRsvp;
    if (!mine || mine.status !== 'yes') {
      confirmSlot.replaceChildren();
      return;
    }

    if (mine.confirmedDayof) {
      // No button: there is nothing left to press. On a card it is two words;
      // the detail page has room for when it happened, and a member checking
      // whether their tap registered is exactly who wants that timestamp.
      const when = mine.confirmedDate
        ? fmtInstant(mine.confirmedDate, viewerZone, TimeFormat.INSTANT)
        : null;
      confirmSlot.replaceChildren(
        el(
          'span',
          {
            class: 'confirmed-tag',
            // The visible text is trimmed on a card, so the full fact goes in
            // the title for anyone hovering or using a screen reader.
            title: when ? `Confirmed for the day at ${when}` : 'Confirmed for the day',
          },
          opts.compact === true || !when ? '✓ Confirmed' : `✓ Confirmed ${when}`,
        ),
      );
      return;
    }

    // Same rule the server enforces, so the button is never offered when the
    // request would be rejected.
    const ended = eventHasEnded(
      event.eventDate,
      event.endDate,
      event.endTime,
      event.timeZone,
      Date.now(),
    );
    if (ended) {
      confirmSlot.replaceChildren();
      return;
    }

    // Only near the event. A "still in?" prompt three weeks out is meaningless —
    // the question is about today.
    const startsAt = event.startsAtUtc ? Date.parse(`${event.startsAtUtc.replace(' ', 'T')}Z`) : null;
    if (startsAt !== null && startsAt - Date.now() > CONFIRM_WINDOW_MS) {
      confirmSlot.replaceChildren();
      return;
    }

    // One control, no heading. "Still in?" is the whole question, and it reads
    // as a question because it ends in one — the old version spent a label line
    // on "Still coming?" to introduce a button that then said the answer.
    const btn = el(
      'button',
      { type: 'button', class: 'btn-confirm-pill', title: 'Confirm you are still coming today' },
      'Still in?',
    );
    btn.addEventListener('click', async () => {
      status.hidden = true;
      btn.setAttribute('disabled', 'disabled');
      try {
        const result = await confirmDayof(event.eventId);
        // Rebuild counts locally from the response rather than refetching. Note
        // `status` comes back untouched — the confirm never rewrites it.
        apply({
          ...counts,
          dayofConfirmed: counts.dayofConfirmed + (result.alreadyConfirmed ? 0 : 1),
          myRsvp: {
            status: result.status,
            confirmedDayof: true,
            confirmedDate: result.confirmedDate,
          },
        });
      } catch (err) {
        status.className = 'form-error';
        fail(err);
        btn.removeAttribute('disabled');
      }
    });

    confirmSlot.replaceChildren(btn);
  }

  renderButtons();
  renderConfirm();
  // No `confirmSlot` here — renderButtons() owns it now, inside the status row.
  root.append(buttons, headcount, status);
  return root;
}

/**
 * The message for the spot beside the buttons: the event's own "it filled up"
 * or "minimum reached" copy, the same words that were posted to the feed,
 * placeholders filled from the live counts. Full wins over minimum, since a
 * full event has necessarily met its minimum. Below the minimum it shows the `needs_min` copy, falling back to
 * a plain "Needs N". No `ended` copy: an ended event is not in the panel.
 */
function capacityNote(event: EventSummary, counts: RsvpCounts): string {
  const vars = {
    title: event.title,
    min: counts.rsvpMin ?? '',
    max: counts.rsvpMax ?? '',
    count: counts.yesCount,
  };
  if (counts.isFull) {
    return eventTypeMessage(event.eventType, 'full', event.typeMessages, vars) ?? 'Full';
  }
  if (counts.rsvpMin !== null) {
    if (!counts.minMet) {
      return (
        eventTypeMessage(event.eventType, 'needs_min', event.typeMessages, vars) ??
        `Needs ${counts.rsvpMin}`
      );
    }
    return (
      eventTypeMessage(event.eventType, 'min_met', event.typeMessages, vars) ??
      `Minimum of ${counts.rsvpMin} met`
    );
  }
  return '';
}

/**
 * The headcount line. Two numbers, always separate.
 *
 * Returns a fresh element each call so `apply()` can swap its children without
 * the caller holding a second reference.
 */
function headcountLine(counts: RsvpCounts, compact: boolean, noteBeside = false): HTMLElement {
  const parts: string[] = [
    counts.rsvpMax === null
      ? `${counts.yesCount} yes`
      : `${counts.yesCount} of ${counts.rsvpMax} yes`,
  ];
  if (counts.dayofConfirmed > 0) parts.push(`${counts.dayofConfirmed} confirmed day-of`);
  if (!compact) {
    if (counts.maybeCount > 0) parts.push(`${counts.maybeCount} maybe`);
    if (counts.noCount > 0) parts.push(`${counts.noCount} no`);
  }
  if (counts.rsvpMin !== null && !noteBeside) {
    parts.push(counts.minMet ? `minimum of ${counts.rsvpMin} met` : `needs ${counts.rsvpMin}`);
  }
  return el('p', { class: 'muted small' }, parts.join(' · '));
}
